import { Object as FabricObject } from 'fabric/fabric-impl';
import mediaManagerTemplateDataMapper from 'modules/core/domain/data-mappers/media-manager-template-data-mapper';
import mediaManagerTemplateGroupDataMapper from 'modules/manager/domain/data-mappers/media-manager-template-group-data-mapper';
import {
  CopyMediaManagerTemplate,
  CreateMediaManagerTemplate,
  MediaManagerTemplateFilter,
  UpdateMediaManagerTemplate,
} from 'modules/core/domain/interfaces/supabase/media-manager/actions';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import graphqlResource from 'modules/core/graphql-resource';
import {
  COPY_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION,
  COPY_MEDIA_MANAGER_TEMPLATE_MUTATION,
  CREATE_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION,
  CREATE_MEDIA_MANAGER_TEMPLATE_MUTATION,
  DELETE_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION,
  DELETE_MEDIA_MANAGER_TEMPLATE_MUTATION,
  MEDIA_MANAGER_PRESETS_QUERY,
  MEDIA_MANAGER_PRESET_TEMPLATE_GROUPS_QUERY,
  MEDIA_MANAGER_TEMPLATES_QUERY,
  MEDIA_MANAGER_TEMPLATE_GROUPS_QUERY,
  MEDIA_MANAGER_TEMPLATE_GROUP_QUERY,
  MEDIA_MANAGER_TEMPLATE_QUERY,
  MediaManagerTemplateGql,
  MediaManagerTemplateGroupGql,
  UPDATE_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION,
  UPDATE_MEDIA_MANAGER_TEMPLATE_MUTATION,
  unwrapGraphqlData,
} from 'modules/manager/domain/graphql';
import { MediaManagerTemplateGroup } from 'modules/manager/domain/interfaces/media-manager-template-group';
import { MatchLayoutSettings } from 'modules/manager/domain/match-layout';
import { PlayerRankingSettings } from 'modules/manager/domain/player-stats';

export interface UpdateTemplateObjectsInput {
  id: number;
  objects: FabricObject[];
  version: string;
}

export interface UpdateTemplateMatchLayoutInput {
  id: number;
  matchLayout: MatchLayoutSettings;
}

export interface UpdateTemplatePlayerRankingInput {
  id: number;
  playerRanking: PlayerRankingSettings;
}

export class MediaManagerRepository {
  async getTemplates(filter?: MediaManagerTemplateFilter): Promise<MediaManagerTemplate[]> {
    if (filter?.id !== undefined) {
      return [await this.getTemplateById(filter.id)];
    }
    if (filter?.tournamentId !== undefined) {
      const envelope = await graphqlResource.query<{
        data: { mediaManagerTemplates: MediaManagerTemplateGql[] };
        errors?: { message: string; extensions?: { code?: number } }[];
      }>(MEDIA_MANAGER_TEMPLATES_QUERY, {
        tournamentId: filter.tournamentId,
        sportType: filter.sportType,
        templateGroupId: filter.templateGroupId,
      });
      const data = unwrapGraphqlData(envelope.data);
      return data.mediaManagerTemplates.map(mediaManagerTemplateDataMapper);
    }
    const envelope = await graphqlResource.query<{
      data: { mediaManagerPresets: MediaManagerTemplateGql[] };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(MEDIA_MANAGER_PRESETS_QUERY, { sportType: filter?.sportType });
    const data = unwrapGraphqlData(envelope.data);
    return data.mediaManagerPresets.map(mediaManagerTemplateDataMapper);
  }

  async getTemplateById(id: number): Promise<MediaManagerTemplate> {
    const envelope = await graphqlResource.query<{
      data: { mediaManagerTemplate: MediaManagerTemplateGql };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(MEDIA_MANAGER_TEMPLATE_QUERY, { id });
    const data = unwrapGraphqlData(envelope.data);
    return mediaManagerTemplateDataMapper(data.mediaManagerTemplate);
  }

  async createTemplate(input: CreateMediaManagerTemplate): Promise<MediaManagerTemplate> {
    const envelope = await graphqlResource.query<{
      data: { createMediaManagerTemplate: MediaManagerTemplateGql };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(CREATE_MEDIA_MANAGER_TEMPLATE_MUTATION, { input });
    const data = unwrapGraphqlData(envelope.data);
    return mediaManagerTemplateDataMapper(data.createMediaManagerTemplate);
  }

  async updateTemplate(input: UpdateMediaManagerTemplate): Promise<MediaManagerTemplate> {
    const envelope = await graphqlResource.query<{
      data: { updateMediaManagerTemplate: MediaManagerTemplateGql };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(UPDATE_MEDIA_MANAGER_TEMPLATE_MUTATION, { input });
    const data = unwrapGraphqlData(envelope.data);
    return mediaManagerTemplateDataMapper(data.updateMediaManagerTemplate);
  }

  async deleteTemplate(id: number): Promise<void> {
    const envelope = await graphqlResource.query<{
      data: { deleteMediaManagerTemplate: { status: string } };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(DELETE_MEDIA_MANAGER_TEMPLATE_MUTATION, { id });
    unwrapGraphqlData(envelope.data);
  }

  async copyTemplate(input: CopyMediaManagerTemplate): Promise<MediaManagerTemplate> {
    const envelope = await graphqlResource.query<{
      data: { copyMediaManagerTemplate: MediaManagerTemplateGql };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(COPY_MEDIA_MANAGER_TEMPLATE_MUTATION, { input });
    const data = unwrapGraphqlData(envelope.data);
    return mediaManagerTemplateDataMapper(data.copyMediaManagerTemplate);
  }

  async getTemplateGroups(tournamentId: number): Promise<MediaManagerTemplateGroup[]> {
    const envelope = await graphqlResource.query<{
      data: { mediaManagerTemplateGroups: MediaManagerTemplateGroupGql[] };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(MEDIA_MANAGER_TEMPLATE_GROUPS_QUERY, { tournamentId });
    const data = unwrapGraphqlData(envelope.data);
    return data.mediaManagerTemplateGroups.map(mediaManagerTemplateGroupDataMapper);
  }

  async getPresetTemplateGroups(): Promise<MediaManagerTemplateGroup[]> {
    const envelope = await graphqlResource.query<{
      data: { mediaManagerPresetTemplateGroups: MediaManagerTemplateGroupGql[] };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(MEDIA_MANAGER_PRESET_TEMPLATE_GROUPS_QUERY);
    const data = unwrapGraphqlData(envelope.data);
    return data.mediaManagerPresetTemplateGroups.map(mediaManagerTemplateGroupDataMapper);
  }

  async getTemplateGroupById(id: number): Promise<MediaManagerTemplateGroup> {
    const envelope = await graphqlResource.query<{
      data: { mediaManagerTemplateGroup: MediaManagerTemplateGroupGql };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(MEDIA_MANAGER_TEMPLATE_GROUP_QUERY, { id });
    const data = unwrapGraphqlData(envelope.data);
    return mediaManagerTemplateGroupDataMapper(data.mediaManagerTemplateGroup);
  }

  async createTemplateGroup(input: { name: string; tournamentId?: number | null }): Promise<MediaManagerTemplateGroup> {
    const envelope = await graphqlResource.query<{
      data: { createMediaManagerTemplateGroup: MediaManagerTemplateGroupGql };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(CREATE_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION, { input });
    const data = unwrapGraphqlData(envelope.data);
    return mediaManagerTemplateGroupDataMapper(data.createMediaManagerTemplateGroup);
  }

  async updateTemplateGroup(input: { id: number; name?: string }): Promise<MediaManagerTemplateGroup> {
    const envelope = await graphqlResource.query<{
      data: { updateMediaManagerTemplateGroup: MediaManagerTemplateGroupGql };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(UPDATE_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION, { input });
    const data = unwrapGraphqlData(envelope.data);
    return mediaManagerTemplateGroupDataMapper(data.updateMediaManagerTemplateGroup);
  }

  async deleteTemplateGroup(id: number, deleteTemplates = false): Promise<void> {
    const envelope = await graphqlResource.query<{
      data: { deleteMediaManagerTemplateGroup: { status: string } };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(DELETE_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION, { id, deleteTemplates });
    unwrapGraphqlData(envelope.data);
  }

  async copyTemplateGroup(input: {
    templateGroupId: number;
    tournamentId: number;
    name?: string;
  }): Promise<MediaManagerTemplateGroup> {
    const envelope = await graphqlResource.query<{
      data: { copyMediaManagerTemplateGroup: MediaManagerTemplateGroupGql };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(COPY_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION, { input });
    const data = unwrapGraphqlData(envelope.data);
    return mediaManagerTemplateGroupDataMapper(data.copyMediaManagerTemplateGroup);
  }

  updateTemplateObjects(input: UpdateTemplateObjectsInput): Promise<MediaManagerTemplate> {
    return this.updateTemplate({
      id: input.id,
      objects: input.objects,
      version: input.version,
    });
  }

  updateTemplateMatchLayout(input: UpdateTemplateMatchLayoutInput): Promise<MediaManagerTemplate> {
    return this.updateTemplate({
      id: input.id,
      matchLayout: input.matchLayout,
    });
  }

  updateTemplatePlayerRanking(input: UpdateTemplatePlayerRankingInput): Promise<MediaManagerTemplate> {
    return this.updateTemplate({
      id: input.id,
      playerRanking: input.playerRanking,
    });
  }
}

const mediaManagerRepository = new MediaManagerRepository();
export default mediaManagerRepository;
